import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { User } from 'lucide-react';
import resumeAPI from '../services/api';
import TemplateSelector from '../components/templates/TemplateSelector';

const ResumePreviewHiddenPage = () => {
  const { resumeId, publicLinkId } = useParams();
  const [searchParams] = useSearchParams();
  const queryResumeId = (searchParams.get('rid') || searchParams.get('resume_id') || '').trim();
  const queryPublicId = (searchParams.get('public') || searchParams.get('pid') || '').trim();
  const previewKey = (searchParams.get('preview_key') || '').trim();
  const exportToken = (searchParams.get('export_token') || '').trim();
  const templateOverride = (searchParams.get('template') || '').trim();
  const resolvedResumeId = (resumeId || queryResumeId || '').trim();
  const resolvedPublicId = (publicLinkId || queryPublicId || '').trim();
  const printParam = (searchParams.get('print') || '').trim().toLowerCase();
  const shouldPrint = !exportToken && (printParam === '1' || printParam === 'true' || printParam === 'yes');
  const shouldWaitForRender = shouldPrint || !!exportToken;
  const didPrintRef = useRef(false);
  const resumePreviewRef = useRef(null);
  const [isRenderReady, setIsRenderReady] = useState(false);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadResume = async () => {
      setLoading(true);
      setError('');
      try {
        const applyTemplateOverride = (data) => {
          if (!data) return data;
          if (!templateOverride) return data;
          return { ...data, template: templateOverride };
        };

        if (exportToken && resolvedResumeId) {
          const exported = await resumeAPI.fetchResumeExport(resolvedResumeId, exportToken);
          const exportedResume = exported?.resume || exported;
          setResume(applyTemplateOverride(exportedResume));
          return;
        }

        if (previewKey) {
          try {
            const raw = localStorage.getItem(previewKey);
            if (raw) {
              const payload = JSON.parse(raw);
              const createdAt = payload?.createdAt || 0;
              const ageMs = Date.now() - createdAt;
              if (payload?.resume && ageMs < 5 * 60 * 1000) {
                setResume(applyTemplateOverride(payload.resume));
                localStorage.removeItem(previewKey);
                return;
              }
            }
          } catch (e) {
            console.warn('Failed to load preview data:', e);
          }
        }
        if (resolvedPublicId) {
          const list = await resumeAPI.fetchByPublicLink(resolvedPublicId);
          setResume(applyTemplateOverride(list?.[0] || null));
        } else if (resolvedResumeId) {
          // Try public endpoint first for direct resume ID access
          try {
            const data = await resumeAPI.fetchPublicResume?.(resolvedResumeId);
            if (data) {
              setResume(applyTemplateOverride(data));
              return;
            }
          } catch (publicErr) {
            console.warn('Public resume fetch failed, trying authenticated:', publicErr);
          }
          // Fallback to authenticated fetch with full data
          const list = await (resumeAPI.fetchAllBestEffortForCurrentUser?.() || resumeAPI.fetchAll({ full: true }));
          const found = list.find((r) => (r.id || r._id) === resolvedResumeId);
          setResume(applyTemplateOverride(found || null));
        } else {
          const list = await (resumeAPI.fetchAllBestEffortForCurrentUser?.() || resumeAPI.fetchAll({ full: true }));
          const found = list.find((r) => r.is_primary) || list[0];
          setResume(applyTemplateOverride(found || null));
        }
      } catch (e) {
        console.error('Failed to load resume preview', e);
        setError('Failed to load resume');
      } finally {
        setLoading(false);
      }
    };
    loadResume();
  }, [resolvedResumeId, resolvedPublicId, previewKey]);

  useEffect(() => {
    if (!resume || !shouldWaitForRender) return;
    setIsRenderReady(false);
    const waitForPaint = () =>
      new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const waitForFonts = async () => {
      try {
        // eslint-disable-next-line no-undef
        await document?.fonts?.ready;
      } catch {
        // ignore
      }
    };

    const hasVisibleContent = (rootEl) => {
      if (!rootEl) return false;
      const text = rootEl.textContent?.trim() || '';
      if (text.length > 0) return true;
      return rootEl.querySelector('*') !== null;
    };

    const waitForImages = async (rootEl) => {
      try {
        if (!rootEl) return;
        const imgs = Array.from(rootEl.querySelectorAll('img'));
        if (imgs.length === 0) return;
        await Promise.all(
          imgs.map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete) return resolve();
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              })
          )
        );
      } catch {
        // ignore
      }
    };

    let cancelled = false;
    const run = async () => {
      const maxAttempts = 6;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) return;
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 250));
        }
        await waitForPaint();
        const rootEl = resumePreviewRef.current;
        if (!hasVisibleContent(rootEl)) {
          continue;
        }
        await waitForFonts();
        await waitForImages(rootEl);
        await waitForPaint();
        if (cancelled) return;
        if (hasVisibleContent(rootEl)) {
          setIsRenderReady(true);
          return;
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [resume, shouldWaitForRender]);

  useEffect(() => {
    if (!shouldPrint || !isRenderReady || didPrintRef.current) return;
    didPrintRef.current = true;
    window.print();
  }, [shouldPrint, isRenderReady]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-gray-500">Loading resume preview...</div>
      </div>
    );
  }

  if (error || !resume) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-gray-500">{error || 'Resume not found'}</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white"
      id="print-resume-preview"
      data-render-ready={isRenderReady ? 'true' : 'false'}
    >
      {/* View Profile Button - Portaled to body so it stays fixed to the viewport regardless of any ancestor transform/filter, and hidden on print */}
      {resume?.profile_slug && typeof document !== 'undefined' && createPortal(
        <div
          className="print:hidden"
          style={{ position: 'fixed', top: '1.25rem', left: '384px', zIndex: 9999 }}
        >
          <Link
            to={`/in/${resume.profile_slug}`}
            className="btn btn-primary inline-flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow"
          >
            <User className="w-4 h-4" />
            <span>View Profile</span>
          </Link>
        </div>,
        document.body
      )}

      {/* Resume Preview */}
      <div id="resume-preview" className="resume-container border border-gray-200">
        <div ref={resumePreviewRef}>
          <TemplateSelector template={resume.template} resume={resume} />
        </div>
      </div>
    </div>
  );
};

export default ResumePreviewHiddenPage;
